/**
 * هديّة عيد الميلاد التلقائيّة.
 *
 * - الأدمن يضبط مرّة واحدة: تفعيل + قائمة handles المنتجات المرشَّحة + نافذة الاستلام.
 * - cron يوميّ ينشئ عرضاً لكلّ مستخدم عيد ميلاده اليوم (idempotent عبر UNIQUE(user_id,year)).
 * - يُرسل إيميل + إشعار داخل الحساب برابط /account/gifts/birthday/$offerId.
 * - العميل يختار من القائمة → status='claimed'، الأدمن يراها في لوحة pending fulfillment.
 * - تنتهي الصلاحيّة بعد window_days → status='expired'.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { resendBirthdayGiftOfferDelivery } from "@/utils/birthdayGifts.server";
import { assertAdmin } from "@/utils/adminAuth.server";

// ─────────────────────────────────────────────────────────────────────────────
// Settings helpers
// ─────────────────────────────────────────────────────────────────────────────

async function readSetting<T>(key: string, fallback: T): Promise<T> {
  const { data } = await supabaseAdmin
    .from("app_settings")
    .select("value")
    .eq("key", key)
    .maybeSingle();
  if (!data) return fallback;
  return data.value as T;
}

async function writeSetting(key: string, value: unknown): Promise<void> {
  await supabaseAdmin
    .from("app_settings")
    .upsert({ key, value: value as any }, { onConflict: "key" });
}

async function requireAdmin(userId: string) {
  await assertAdmin(userId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin: settings
// ─────────────────────────────────────────────────────────────────────────────

export type BirthdayRewardType = "product" | "points" | "cash";

export interface BirthdayBannerConfig {
  enabled: boolean;
  pages: string[]; // ["all"] أو مسارات محدّدة
  position: "top" | "bottom" | "floating-bottom-right" | "floating-bottom-left";
  variant: "strip" | "card" | "pill";
  bgColor?: string;
  textColor?: string;
  textAr?: string;
  textEn?: string;
  ctaAr?: string;
  ctaEn?: string;
  dismissible: boolean;
  showFor: "pending" | "claimed" | "both";
  /** يبقى البانر يظهر بعد انتهاء الصلاحيّة كرسالة «فُرصة فاتت» ليبقى العميل
   *  يفتح الموقع تحسّباً لفرص قادمة. */
  showAfterExpiry: boolean;
  /** مدّة عرض رسالة «فُرصة فاتت» بالأيّام بعد انتهاء العرض. 0 = حتى نهاية السنة. */
  showAfterExpiryDays: number;
  /** نصّ مخصّص لحالة الانتهاء (يَظهر فقط إذا كان showAfterExpiry=true). */
  textArExpired?: string;
  textEnExpired?: string;
}

const DEFAULT_BANNER: BirthdayBannerConfig = {
  enabled: true,
  pages: ["all"],
  position: "top",
  variant: "strip",
  dismissible: true,
  showFor: "pending",
  showAfterExpiry: false,
  showAfterExpiryDays: 7,
};

/**
 * شروط استهداف المستفيدين. mode='all' = أيّ عميل لديه تاريخ ميلاد.
 * mode='filters' = يجب أن يحقّق كلّ الشروط غير الفارغة.
 */
export type BirthdayMarket = "SA" | "AE" | "KW" | "QA" | "BH" | "OM" | "IQ" | "US";

export interface BirthdayEligibility {
  mode: "all" | "filters";
  /** شرائح السفير المسموح بها — فارغ = أيّ شريحة (أو غير سفير). */
  ambassadorTiers: Array<"decouverte" | "active" | "elite">;
  /** يجب أن يكون سفيراً معتمَداً (status='approved'). */
  ambassadorApprovedOnly: boolean;
  /** الأسواق المسموح بها — فارغ = كلّ الأسواق. */
  markets: BirthdayMarket[];
  /** الحدّ الأدنى لعدد الطلبات المدفوعة. 0 = بلا حدّ. */
  minOrdersCount: number;
  /** الحدّ الأدنى لإجماليّ الإنفاق (AED). 0 = بلا حدّ. */
  minTotalSpent: number;
  /** الحدّ الأدنى لعمر الحساب بالأيّام. 0 = بلا حدّ. */
  minAccountAgeDays: number;
}

const DEFAULT_ELIGIBILITY: BirthdayEligibility = {
  mode: "all",
  ambassadorTiers: [],
  ambassadorApprovedOnly: false,
  markets: [],
  minOrdersCount: 0,
  minTotalSpent: 0,
  minAccountAgeDays: 0,
};

export interface BirthdayGiftSettings {
  enabled: boolean;
  productHandles: string[];
  claimWindowDays: number;
  sendOffsetDays: number;
  rewardType: BirthdayRewardType;
  rewardPoints: number;
  rewardCashAmount: number;
  banner: BirthdayBannerConfig;
  eligibility: BirthdayEligibility;
}

export const getBirthdayGiftSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BirthdayGiftSettings> => {
    await requireAdmin(context.userId);
    const [enabled, handles, days, offset, rType, rPoints, rCash, banner, elig] = await Promise.all([
      readSetting<boolean>("birthday_gift_enabled", false),
      readSetting<string[]>("birthday_gift_product_handles", []),
      readSetting<number>("birthday_gift_claim_window_days", 14),
      readSetting<number>("birthday_gift_send_offset_days", 0),
      readSetting<BirthdayRewardType>("birthday_gift_reward_type", "product"),
      readSetting<number>("birthday_gift_reward_points", 100),
      readSetting<number>("birthday_gift_reward_cash_amount", 50),
      readSetting<BirthdayBannerConfig>("birthday_gift_banner", DEFAULT_BANNER),
      readSetting<BirthdayEligibility>("birthday_gift_eligibility", DEFAULT_ELIGIBILITY),
    ]);
    return {
      enabled: Boolean(enabled),
      productHandles: Array.isArray(handles) ? handles : [],
      claimWindowDays: Number(days) || 14,
      sendOffsetDays: Math.max(0, Math.min(30, Number(offset) || 0)),
      rewardType: (["product", "points", "cash"].includes(rType as string)
        ? rType
        : "product") as BirthdayRewardType,
      rewardPoints: Math.max(0, Number(rPoints) || 0),
      rewardCashAmount: Math.max(0, Number(rCash) || 0),
      banner: { ...DEFAULT_BANNER, ...(banner ?? {}) },
      eligibility: { ...DEFAULT_ELIGIBILITY, ...(elig ?? {}) },
    };
  });

const bannerSchema = z.object({
  enabled: z.boolean(),
  pages: z.array(z.string().min(1).max(120)).min(1).max(20),
  position: z.enum(["top", "bottom", "floating-bottom-right", "floating-bottom-left"]),
  variant: z.enum(["strip", "card", "pill"]),
  bgColor: z.string().max(40).optional(),
  textColor: z.string().max(40).optional(),
  textAr: z.string().max(300).optional(),
  textEn: z.string().max(300).optional(),
  ctaAr: z.string().max(80).optional(),
  ctaEn: z.string().max(80).optional(),
  dismissible: z.boolean(),
  showFor: z.enum(["pending", "claimed", "both"]),
  showAfterExpiry: z.boolean(),
  showAfterExpiryDays: z.number().int().min(0).max(365),
  textArExpired: z.string().max(300).optional(),
  textEnExpired: z.string().max(300).optional(),
});

const eligibilitySchema = z.object({
  mode: z.enum(["all", "filters"]),
  ambassadorTiers: z.array(z.enum(["decouverte", "active", "elite"])).max(3),
  ambassadorApprovedOnly: z.boolean(),
  markets: z.array(z.enum(["SA", "AE", "KW", "QA", "BH", "OM", "IQ", "US"])).max(6),
  minOrdersCount: z.number().int().min(0).max(1000),
  minTotalSpent: z.number().min(0).max(1_000_000),
  minAccountAgeDays: z.number().int().min(0).max(3650),
});

const saveSettingsSchema = z.object({
  enabled: z.boolean(),
  productHandles: z.array(z.string().min(1).max(200)).max(20),
  claimWindowDays: z.number().int().min(1).max(60),
  sendOffsetDays: z.number().int().min(0).max(30),
  rewardType: z.enum(["product", "points", "cash"]),
  rewardPoints: z.number().int().min(0).max(100000),
  rewardCashAmount: z.number().min(0).max(100000),
  banner: bannerSchema,
  eligibility: eligibilitySchema,
});

export const saveBirthdayGiftSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => saveSettingsSchema.parse(d))
  .handler(async ({ data, context }) => {
    await requireAdmin(context.userId);
    await Promise.all([
      writeSetting("birthday_gift_enabled", data.enabled),
      writeSetting("birthday_gift_product_handles", data.productHandles),
      writeSetting("birthday_gift_claim_window_days", data.claimWindowDays),
      writeSetting("birthday_gift_send_offset_days", data.sendOffsetDays),
      writeSetting("birthday_gift_reward_type", data.rewardType),
      writeSetting("birthday_gift_reward_points", data.rewardPoints),
      writeSetting("birthday_gift_reward_cash_amount", data.rewardCashAmount),
      writeSetting("birthday_gift_banner", data.banner),
      writeSetting("birthday_gift_eligibility", data.eligibility),
    ]);
    return { ok: true };
  });

/**
 * يقرأ تكوين البانر العامّ — يُستدعى من كلّ زائر/عميل. لا يحتاج admin.
 * يُرجع تكوين البانر فقط دون أيّ بيانات حسّاسة.
 */
export const getBirthdayBannerConfig = createServerFn({ method: "GET" })
  .handler(async (): Promise<BirthdayBannerConfig> => {
    const banner = await readSetting<BirthdayBannerConfig>(
      "birthday_gift_banner",
      DEFAULT_BANNER,
    );
    return { ...DEFAULT_BANNER, ...(banner ?? {}) };
  });

// ─────────────────────────────────────────────────────────────────────────────
// Admin: list offers
// ─────────────────────────────────────────────────────────────────────────────

export interface AdminBirthdayOffer {
  id: string;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  year: number;
  candidate_handles: string[];
  chosen_handle: string | null;
  status: string;
  reward_type: BirthdayRewardType;
  reward_points: number | null;
  reward_cash_amount: number | null;
  reward_cash_currency: string | null;
  expires_at: string;
  claimed_at: string | null;
  fulfilled_at: string | null;
  fulfilled_note: string | null;
  created_at: string;
}

export const listBirthdayGiftOffers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminBirthdayOffer[]> => {
    await requireAdmin(context.userId);
    const { data: offers } = await supabaseAdmin
      .from("birthday_gift_offers")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (!offers || offers.length === 0) return [];

    const userIds = Array.from(new Set(offers.map((o: any) => o.user_id)));
    const { data: profiles } = await supabaseAdmin
      .from("profiles")
      .select("id, display_name")
      .in("id", userIds);
    // الإيميل مصدره Auth (profiles لا يحتوي عمود email)
    const { getUserEmails } = await import("@/lib/server/userEmails.server");
    const emailMap = await getUserEmails(userIds as string[]);
    const profileMap = new Map<string, { name: string | null; email: string | null }>();
    for (const p of profiles ?? []) {
      profileMap.set((p as any).id, {
        name: (p as any).display_name ?? null,
        email: emailMap.get((p as any).id) ?? null,
      });
    }
    return offers.map((o: any) => ({
      id: o.id,
      user_id: o.user_id,
      user_email: profileMap.get(o.user_id)?.email ?? null,
      user_name: profileMap.get(o.user_id)?.name ?? null,
      year: o.year,
      candidate_handles: o.candidate_handles ?? [],
      chosen_handle: o.chosen_handle ?? null,
      status: o.status,
      reward_type: (o.reward_type ?? "product") as BirthdayRewardType,
      reward_points: o.reward_points ?? null,
      reward_cash_amount: o.reward_cash_amount ?? null,
      reward_cash_currency: o.reward_cash_currency ?? null,
      expires_at: o.expires_at,
      claimed_at: o.claimed_at,
      fulfilled_at: o.fulfilled_at,
      fulfilled_note: o.fulfilled_note,
      created_at: o.created_at,
    }));
  });


const fulfillSchema = z.object({
  offerId: z.string().uuid(),
  note: z.string().max(500).optional(),
});

export const markBirthdayGiftFulfilled = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => fulfillSchema.parse(d))
  .handler(async ({ data, context }) => {
    await requireAdmin(context.userId);
    const { error } = await supabaseAdmin
      .from("birthday_gift_offers")
      .update({
        status: "fulfilled",
        fulfilled_at: new Date().toISOString(),
        fulfilled_by: context.userId,
        fulfilled_note: data.note ?? null,
      })
      .eq("id", data.offerId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const offerIdSchema = z.object({ offerId: z.string().uuid() });

/** يعيد إرسال إيميل + إشعار داخل الحساب لعرض pending. */
export const resendBirthdayGiftOffer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => offerIdSchema.parse(d))
  .handler(async ({ data, context }) => {
    await requireAdmin(context.userId);
    await resendBirthdayGiftOfferDelivery({
      offerId: data.offerId,
      userId: context.userId,
    });
    return { ok: true };
  });

/** إلغاء عرض pending — يُحوَّل إلى status='cancelled' فلا يستطيع العميل اختياره. */
export const cancelBirthdayGiftOffer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => offerIdSchema.parse(d))
  .handler(async ({ data, context }) => {
    await requireAdmin(context.userId);
    const { data: offer } = await supabaseAdmin
      .from("birthday_gift_offers")
      .select("id, status")
      .eq("id", data.offerId)
      .maybeSingle();
    if (!offer) throw new Error("NOT_FOUND");
    if (!["pending", "claimed"].includes((offer as any).status)) {
      throw new Error("CANNOT_CANCEL");
    }
    const { error } = await supabaseAdmin
      .from("birthday_gift_offers")
      .update({ status: "cancelled" })
      .eq("id", data.offerId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ─────────────────────────────────────────────────────────────────────────────
// Customer: read + claim
// ─────────────────────────────────────────────────────────────────────────────

export interface MyBirthdayOffer {
  id: string;
  year: number;
  candidate_handles: string[];
  chosen_handle: string | null;
  status: string;
  expires_at: string;
  reward_type: BirthdayRewardType;
  reward_points: number | null;
  reward_cash_amount: number | null;
  reward_cash_currency: string | null;
}

function mapMyOffer(o: any): MyBirthdayOffer {
  return {
    id: o.id,
    year: o.year,
    candidate_handles: o.candidate_handles ?? [],
    chosen_handle: o.chosen_handle ?? null,
    status: o.status,
    expires_at: o.expires_at,
    reward_type: (o.reward_type ?? "product") as BirthdayRewardType,
    reward_points: o.reward_points ?? null,
    reward_cash_amount: o.reward_cash_amount ?? null,
    reward_cash_currency: o.reward_cash_currency ?? null,
  };
}

const getMyOfferSchema = z.object({ offerId: z.string().uuid() });

export const getMyBirthdayGiftOffer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => getMyOfferSchema.parse(d))
  .handler(async ({ data, context }): Promise<MyBirthdayOffer | null> => {
    const { data: offer } = await supabaseAdmin
      .from("birthday_gift_offers")
      .select("id, user_id, year, candidate_handles, chosen_handle, status, expires_at, reward_type, reward_points, reward_cash_amount, reward_cash_currency")
      .eq("id", data.offerId)
      .maybeSingle();
    if (!offer || (offer as any).user_id !== context.userId) return null;
    return mapMyOffer(offer);
  });

/** يعيد أحدث عرض هديّة عيد ميلاد متاح للعميل.
 *  - pending/claimed: ما دام لم تنتهِ صلاحيّته.
 *  - expired: فقط إذا فعّل الأدمن showAfterExpiry، وضمن نافذة showAfterExpiryDays
 *    (0 = حتى نهاية السنة الميلاديّة). */
export const getMyPendingBirthdayGift = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MyBirthdayOffer | null> => {
    const nowIso = new Date().toISOString();
    // (1) جرّب عرضاً نشطاً أوّلاً
    const { data: active } = await supabaseAdmin
      .from("birthday_gift_offers")
      .select("id, user_id, year, candidate_handles, chosen_handle, status, expires_at, reward_type, reward_points, reward_cash_amount, reward_cash_currency")
      .eq("user_id", context.userId)
      .in("status", ["pending", "claimed"])
      .gt("expires_at", nowIso)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (active) return mapMyOffer(active);

    // (2) لا يوجد عرض نشط → تحقّق من إعداد «إبقاء البانر بعد الانتهاء»
    const banner = await readSetting<BirthdayBannerConfig>(
      "birthday_gift_banner",
      DEFAULT_BANNER,
    );
    if (!banner?.showAfterExpiry) return null;

    const days = Math.max(0, Number(banner.showAfterExpiryDays) || 0);
    // العتبة الزمنية: العرض انتهى بعد (الآن − days). إذا days=0 → بلا حدّ زمنيّ.
    const cutoffIso =
      days > 0
        ? new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
        : null;

    let q = supabaseAdmin
      .from("birthday_gift_offers")
      .select("id, user_id, year, candidate_handles, chosen_handle, status, expires_at, reward_type, reward_points, reward_cash_amount, reward_cash_currency")
      .eq("user_id", context.userId)
      .eq("status", "expired")
      .order("expires_at", { ascending: false })
      .limit(1);
    if (cutoffIso) q = q.gte("expires_at", cutoffIso);

    const { data: expired } = await q.maybeSingle();
    if (!expired) return null;
    return mapMyOffer(expired);
  });


const claimSchema = z.object({
  offerId: z.string().uuid(),
  /** مطلوب فقط لـreward_type='product'؛ يُتجاهَل في points/cash. */
  handle: z.string().min(1).max(200).optional(),
});

export const claimBirthdayGiftOffer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => claimSchema.parse(d))
  .handler(async ({ data, context }) => {
    const { data: offer } = await supabaseAdmin
      .from("birthday_gift_offers")
      .select("id, user_id, status, expires_at, candidate_handles, reward_type, reward_points")
      .eq("id", data.offerId)
      .maybeSingle();
    if (!offer || (offer as any).user_id !== context.userId) {
      throw new Error("NOT_FOUND");
    }
    if ((offer as any).status !== "pending") throw new Error("ALREADY_CLAIMED");
    if (new Date((offer as any).expires_at) < new Date()) throw new Error("EXPIRED");

    const rewardType = ((offer as any).reward_type ?? "product") as BirthdayRewardType;
    const nowIso = new Date().toISOString();

    if (rewardType === "product") {
      if (!data.handle || !((offer as any).candidate_handles ?? []).includes(data.handle)) {
        throw new Error("INVALID_HANDLE");
      }
      const { error } = await supabaseAdmin
        .from("birthday_gift_offers")
        .update({
          chosen_handle: data.handle,
          status: "claimed",
          claimed_at: nowIso,
        })
        .eq("id", data.offerId);
      if (error) throw new Error(error.message);

      // 🎁 إنشاء عنصر محفظة الهدايا — العميل سيختار طريقة التسليم
      try {
        const { createGiftWalletItemFromBirthday } = await import(
          "@/lib/giftWallet.functions"
        );
        await createGiftWalletItemFromBirthday({
          offerId: data.offerId,
          userId: context.userId,
          productHandle: data.handle,
          expiresAt: (offer as any).expires_at,
        });
      } catch (e) {
        console.error("createGiftWalletItemFromBirthday failed:", e);
      }

      return { ok: true, autoFulfilled: false };
    }

    if (rewardType === "points") {
      const pts = Number((offer as any).reward_points) || 0;
      if (pts > 0) {
        await supabaseAdmin.from("customer_loyalty_transactions").insert({
          user_id: context.userId,
          points: pts,
          reason: "admin_grant",
          note: `🎂 هديّة عيد ميلاد #${data.offerId.slice(0, 8)}`,
        });
        // إعادة احتساب رصيد العميل
        await supabaseAdmin.rpc("recalc_customer_loyalty" as any, { _user_id: context.userId });
      }
      const { error } = await supabaseAdmin
        .from("birthday_gift_offers")
        .update({
          status: "fulfilled",
          claimed_at: nowIso,
          fulfilled_at: nowIso,
          fulfilled_note: `auto: +${pts} نقطة`,
        })
        .eq("id", data.offerId);
      if (error) throw new Error(error.message);
      return { ok: true, autoFulfilled: true };
    }

    // cash → claimed (الأدمن يولّد الكوبون يدويّاً ثمّ يضغط «تمّ الإرسال»)
    const { error } = await supabaseAdmin
      .from("birthday_gift_offers")
      .update({ status: "claimed", claimed_at: nowIso })
      .eq("id", data.offerId);
    if (error) throw new Error(error.message);
    return { ok: true, autoFulfilled: false };
  });

// ─────────────────────────────────────────────────────────────────────────────
// Cron: process today's birthdays — moved to birthdayGifts.server.ts to keep
// dynamic emailSend import out of the client bundle (import-protection).
// ─────────────────────────────────────────────────────────────────────────────


